import React, { useState, useEffect, useMemo, useRef } from 'react';

const FindRecipesByIngredientsManager = ({ recipes }) => {
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [ingredientSearchTerm, setIngredientSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [possibleRecipes, setPossibleRecipes] = useState([]);
  const searchInputRef = useRef(null);

  const allIngredients = useMemo(() => {
    const ingredientsSet = new Set();
    recipes.forEach(recipe => {
      if (recipe.ingredients) {
        recipe.ingredients.forEach(ingredient => {
          ingredientsSet.add(ingredient.name);
        });
      }
    });
    return Array.from(ingredientsSet).sort();
  }, [recipes]);

  useEffect(() => {
    const available = selectedIngredients.map(s => s.trim().toLowerCase());

    if (available.length === 0) {
      setPossibleRecipes([]);
      return;
    }

    const findPossible = () => {
      const possible = recipes
        .map(recipe => {
          const ingredients = recipe.ingredients || [];
          const ownedIngredients = ingredients.filter(i => available.includes(i.name.toLowerCase()));

          if (ownedIngredients.length === 0) {
            return null;
          }

          const missingIngredients = ingredients.filter(i => !available.includes(i.name.toLowerCase()));

          return {
            ...recipe,
            missingIngredients,
            ownedCount: ownedIngredients.length,
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (b.ownedCount !== a.ownedCount) {
            return b.ownedCount - a.ownedCount;
          }
          return a.missingIngredients.length - b.missingIngredients.length;
        });
      setPossibleRecipes(possible);
    };

    const timerId = setTimeout(findPossible, 300);
    return () => clearTimeout(timerId);

  }, [selectedIngredients, recipes]);

  const handleAddIngredient = (ingredientName) => {
    if (selectedIngredients.map(s => s.toLowerCase()).includes(ingredientName.toLowerCase())) {
      return;
    }
    setSelectedIngredients([...selectedIngredients, ingredientName]);
  };

  const handleIngredientSelect = (ingredientName) => {
    if (!selectedIngredients.map(s => s.toLowerCase()).includes(ingredientName.toLowerCase())) {
      setSelectedIngredients([...selectedIngredients, ingredientName]);
    }
    setIngredientSearchTerm('');
    setIsDropdownOpen(false);
  };

  const handleRemoveIngredient = (ingredientName) => {
    setSelectedIngredients(selectedIngredients.filter(name => name !== ingredientName));
  };

  const filteredIngredients = allIngredients
    .filter(name =>
      name.toLowerCase().includes(ingredientSearchTerm.toLowerCase()) &&
      !selectedIngredients.map(s => s.toLowerCase()).includes(name.toLowerCase())
    );

  return (
    <div className="section">
      <div className="find-recipes-by-ingredients-title-container">
        <i className="material-icons" style={{ marginRight: '5px' }}>lightbulb</i>
        <h2>What to make with...</h2>
      </div>
      <div className="searchable-dropdown" onBlur={() => setTimeout(() => setIsDropdownOpen(false), 100)}>
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Enter available ingredients..."
            value={ingredientSearchTerm}
            onChange={e => {
              setIngredientSearchTerm(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            onKeyDown={e => {
              if (e.key === 'Enter' && filteredIngredients.length > 0) {
                handleIngredientSelect(filteredIngredients[0]);
              }
            }}
          />
          {ingredientSearchTerm && <button onClick={() => { setIngredientSearchTerm(''); searchInputRef.current.focus(); }} className="clear-input-button" title="Clear search"><i className="material-icons">close</i></button>}
        </div>
        {isDropdownOpen && filteredIngredients.length > 0 && (
          <ul className="dropdown-list">
            {filteredIngredients.map(name => (
              <li key={name} onMouseDown={() => handleIngredientSelect(name)}>
                {name}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="selected-items">
        {selectedIngredients.map(name => (
          <div key={name} className="selected-item">
            {name}
            <button title="Remove ingredient" onClick={() => handleRemoveIngredient(name)} className="remove-item-button"><i className="material-icons">close</i></button>
          </div>
        ))}
      </div>
      {possibleRecipes.length > 0 && (
        <div className="possible-recipes">
          <h3>Possible Recipes:</h3>
          <ul>
            {possibleRecipes.map(recipe => (
              <li key={recipe.id} className="possible-recipe-item">
                <strong>{recipe.name}</strong>
                <div className="missing-ingredients">
                  {recipe.missingIngredients.length > 0 ? (
                    <><span>Missing: </span>{recipe.missingIngredients.map((ing, index) => (<button key={index} className="missing-ingredient-button" onClick={() => handleAddIngredient(ing.name)} title={`Add ${ing.name} to your ingredients`}>{ing.name}</button>))}</>
                  ) : (<div className="all-ingredients-present"><span>You have all the ingredients for this recipe!</span></div>)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FindRecipesByIngredientsManager;